import { useEffect, useMemo, useState } from "react";

import { ReportSection } from "../../card/SiteReport/Sections";
import { SiteOverview } from "../../typings";
import { useSiteById } from "../useSiteById";

type ReportLoadStatus = { [key in ReportSection]?: boolean };

export type useSiteReportResult = {
  site?: SiteOverview;
  notes: string;
  onReportSectionLoaded: (reportName: ReportSection) => void;
  isVisible: (sectionName: ReportSection) => boolean;
  isDesignationReportVisible: boolean;
  visibleSections: ReportSection[];
  printViewClassNames: string;
  loading: boolean;
  logoUrl: string | null;
};

export function useSiteReport(
  siteCardId: string | null,
  visibleReports: ReportSection[],
  trigger?: number
): useSiteReportResult {
  const params = useMemo(() => new URLSearchParams(document.location.search), []);
  const [notes, setNotes] = useState<string>(
    params.get("notes") ? String(params.get("notes")) : ""
  );
  const isPrintView = Boolean(params.get("isPrint"));

  const siteId = siteCardId ?? params.get("siteId");

  const { site } = useSiteById(siteId);
  useEffect(() => {
    if (!site?.notes) return;
    setNotes(site.notes);
  }, [site]);

  const visibleSections = useMemo(() => {
    if (isPrintView) {
      return (params.get("reports")?.split(",") ?? []) as ReportSection[];
    }
    return visibleReports;
  }, [isPrintView, params, visibleReports]);

  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    setLogoUrl(localStorage.getItem(siteId + "_logo"));
  }, [siteId, trigger]);

  const [reportLoadStatus, setReportLoadStatus] = useState<ReportLoadStatus>({});

  const onReportSectionLoaded = (reportName: ReportSection) => {
    setReportLoadStatus((prev) => {
      if (!Object.keys(prev).includes(reportName)) {
        return { ...prev, [reportName]: true };
      }
      return prev;
    });
  };

  const isVisible = (sectionName: ReportSection) => visibleSections.includes(sectionName);

  const isReportSectionReadyToPrint = (sectionName: ReportSection) =>
    !isVisible(sectionName) || reportLoadStatus[sectionName];

  const isUSDesignationSectionsVisible = [
    ReportSection.ZONING,
    ReportSection.HAZARDS,
    ReportSection.DEMOGRAPHICS,
    ReportSection.PERMITS,
  ].some(isVisible);
  const isDesignationReportVisible =
    isVisible(ReportSection.DESIGNATIONS) ||
    isUSDesignationSectionsVisible ||
    isVisible(ReportSection.POWER);

  const isDesignationReadyToPrint =
    !isDesignationReportVisible || reportLoadStatus[ReportSection.DESIGNATIONS];

  const isPrintViewLoaded =
    !isPrintView ||
    (reportLoadStatus[ReportSection.BOUNDARIES_SATELLITE] &&
      reportLoadStatus[ReportSection.BOUNDARIES_ROADMAP] &&
      isReportSectionReadyToPrint(ReportSection.OWNERSHIP) &&
      isReportSectionReadyToPrint(ReportSection.PLANNING) &&
      isDesignationReadyToPrint &&
      isReportSectionReadyToPrint(ReportSection.COMPARABLES));

  useEffect(() => {
    if (isPrintView && isPrintViewLoaded) {
      window.print();
    }
  }, [isPrintViewLoaded, isPrintView]);

  addEventListener("afterprint", () => window.close());

  const printViewClassNames = isPrintView ? "atlas-invisible print:atlas-visible" : "atlas-visible";

  return {
    site,
    notes,
    onReportSectionLoaded,
    isVisible,
    isDesignationReportVisible,
    visibleSections,
    printViewClassNames,
    loading: isPrintView && !isPrintViewLoaded,
    logoUrl,
  };
}
